/** "What's New" floating announcement card — the content layer that pairs with the update prompt.
 *
 *  UpdateNotificationBanner gets the user onto a new build ("Update ready" →
 *  refresh / silent reload). This component tells them what that build changed.
 *
 *  Behaviour (Slack/Notion model):
 *   - Once signed in, compare the running version (APP_BUILD_INFO.version)
 *     against the last version the user has seen (localStorage). If there are
 *     documented notes in between, auto-open the sheet ONCE, then mark the
 *     running version as seen so it never re-shows. The auto-pop (and the
 *     seen-marker write) waits for auth — it must never appear over the login
 *     screen, and marking notes seen pre-login would swallow them entirely.
 *   - First-ever visit (no stored version) shows the current release's notes
 *     once, then records the version. Existing users get no "before" to diff
 *     against, so without this the inaugural release after launch would never
 *     surface; capping it to the current version keeps brand-new users from a
 *     wall of history. Skipped when storage can't persist the marker, so it
 *     can't re-pop every reload.
 *   - Reachable on demand any time via the `quantis:open-whats-new` window
 *     event (e.g. a menu/help entry), which shows the notes regardless.
 *
 *  Notes themselves live in src/release-notes.ts — edit that file to publish.
 *
 *  Native iOS shell: instead of this web card, the chosen notes are handed to
 *  the native shell (presentWhatsNew bridge) which shows them in a real native
 *  sheet, matching the native More overflow. Web / macOS / desktop keep this
 *  card. The show/skip decision stays here; only the notes cross the bridge.
 *
 *  Non-modal + custom backdrop: avoids scroll-lock deadlock when opened after
 *  another modal closes (body pointer-events:none stranding). See JSX comment. */

import { useCallback, useEffect, useState } from 'react';
import { Plus, Wrench, ArrowUp, X } from '@/shared/ui/icons';
import { DrawerCard } from '../../shared/ui/drawer-card';
import { GlassPill } from '../../shared/ui/drawer-card-header';
import { useAuth } from '../auth/AuthContext';
import { NativeBridge } from '../../services/NativeBridge';
import { APP_BUILD_INFO } from '../../utils/appBuildInfo';
import {
  RELEASE_NOTES,
  releaseNotesBetween,
  compareVersions,
  type ReleaseNote,
  type ReleaseNoteCategory,
} from '../../release-notes';

const LAST_SEEN_VERSION_KEY = 'quantis-last-seen-version';

/** Window event any surface can dispatch to open the panel on demand. */
export const OPEN_WHATS_NEW_EVENT = 'quantis:open-whats-new';

function readLastSeenVersion(): string | null {
  if (typeof window === 'undefined') return null;
  try { return window.localStorage.getItem(LAST_SEEN_VERSION_KEY); } catch { return null; }
}

/** Returns whether the marker actually persisted. A read-back catches the
 *  case where setItem neither throws nor stores (some storage-disabled
 *  sessions), so callers can tell "storage works" from "storage unusable". */
function writeLastSeenVersion(version: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(LAST_SEEN_VERSION_KEY, version);
    return window.localStorage.getItem(LAST_SEEN_VERSION_KEY) === version;
  } catch {
    return false;
  }
}

/** Plain-object shape the native shell's presentWhatsNew bridge expects. */
function serializeReleaseNote(note: ReleaseNote) {
  return {
    version: note.version,
    date: note.date ?? '',
    title: note.title,
    items: note.items.map((item) => ({ category: item.category, text: item.text })),
  };
}

// The icon inherits its colour from dotClass via currentColor.
const CATEGORY_META: Record<ReleaseNoteCategory, { label: string; icon: typeof Plus; dotClass: string }> = {
  new: {
    label: 'New',
    icon: Plus,
    dotClass: 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-400',
  },
  improved: {
    label: 'Improved',
    icon: ArrowUp,
    dotClass: 'bg-blue-500/12 text-blue-600 dark:text-blue-400',
  },
  fixed: {
    label: 'Fixed',
    icon: Wrench,
    dotClass: 'bg-amber-500/12 text-amber-600 dark:text-amber-400',
  },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Fixed en-GB rather than the device locale, matching the app's UK
  // formatting convention (see currencyFormat.ts).
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function WhatsNewSheet() {
  const { status: authStatus } = useAuth();
  const [open, setOpen] = useState(false);
  // Which notes to render. For an auto-pop we show only what's between the
  // last-seen and current version; on-demand we show the full changelog.
  const [notes, setNotes] = useState<ReleaseNote[]>([]);

  const currentVersion = APP_BUILD_INFO.version;

  // Present the chosen notes. In a native shell the native side renders them
  // (a popover on macOS, a sheet on iOS — Help → What's New, or an auto-pop on a
  // new version), so hand them over the bridge and never open the web DrawerCard.
  // Everywhere else (browser, PWA, Tauri) the web card opens as before.
  const showNotes = useCallback((toShow: ReleaseNote[], deferOpen = false) => {
    if (toShow.length === 0) return;
    if (NativeBridge.isMacShell() || NativeBridge.isIOSShell()) {
      NativeBridge.presentNativeWhatsNew(toShow.map(serializeReleaseNote));
      return;
    }
    setNotes(toShow);
    if (deferOpen) requestAnimationFrame(() => setOpen(true));
    else setOpen(true);
  }, []);

  // Auto-pop on the first render after landing on a newer build — but only
  // once signed in, so it never opens over the login screen.
  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    if (!currentVersion) return;
    const lastSeen = readLastSeenVersion();

    // First-ever visit has no "before" to diff against. Rather than replay the
    // whole history we show just the current release's notes once — but only
    // if we can record that we've shown them. When storage is unusable (private
    // mode, disabled, quota) the marker can't persist, so the sheet would
    // re-pop on every reload; in that case stay silent like the old path.
    if (lastSeen === null) {
      if (!writeLastSeenVersion(currentVersion)) return;
      const inaugural = RELEASE_NOTES.filter(
        (note) => compareVersions(note.version, currentVersion) === 0,
      );
      if (inaugural.length > 0) {
        showNotes(inaugural);
      }
      return;
    }

    // Returning user: show everything between the version they last saw and
    // the running build.
    const unseen = releaseNotesBetween(lastSeen, currentVersion);
    if (unseen.length > 0) {
      showNotes(unseen);
    } else {
      // Up to date (or this build has no notes) — keep the marker current so
      // we don't re-check the same gap on every boot.
      writeLastSeenVersion(currentVersion);
    }
  }, [authStatus, currentVersion, showNotes]);

  // On-demand open from a menu/help entry — show the whole changelog. Defer the
  // open by a frame: this is usually fired from the Support dialog, and opening
  // the drawer's body-lock in the same tick the dialog is releasing its own can
  // strand the body on pointer-events:none. Letting the dialog close first avoids
  // the handoff deadlock.
  useEffect(() => {
    const onOpen = () => showNotes(RELEASE_NOTES, true);
    window.addEventListener(OPEN_WHATS_NEW_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_WHATS_NEW_EVENT, onOpen);
  }, [showNotes]);

  // The native shell owns dismissal (macOS popover / iOS sheet). When it closes
  // it dispatches this event so we mark the running version seen — mirrors the
  // web card's mark-on-close, and (because it's on dismiss, not on present) a
  // dropped present never swallows the notes; they retry on the next launch.
  useEffect(() => {
    const onDismissed = () => {
      if (currentVersion) writeLastSeenVersion(currentVersion);
    };
    window.addEventListener('quantis:native:whatsNewDismissed', onDismissed);
    return () => window.removeEventListener('quantis:native:whatsNewDismissed', onDismissed);
  }, [currentVersion]);

  // Closing always means "I've seen the current version" — mark caught up so
  // the auto-pop never fires again for this build.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next && currentVersion) writeLastSeenVersion(currentVersion);
  }, [currentVersion]);

  if (RELEASE_NOTES.length === 0) return null;

  return (
    // Same drawer-card primitive the More menu uses: centred iOS title, leading
    // close pill, glass scroll-edge header, grab handle, drag-to-dismiss.
    <DrawerCard
      open={open}
      onOpenChange={handleOpenChange}
      title="What's new"
      description="Recent updates and fixes in Quantis"
      leading={<GlassPill icon={X} ariaLabel="Close" onClick={() => handleOpenChange(false)} />}
      bodyClassName="px-5 pt-1"
    >
      <div
        data-vaul-no-drag
        className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{
          touchAction: 'pan-y',
          overscrollBehaviorY: 'contain',
          paddingBottom: 'max(1.75rem, calc(var(--safe-area-bottom) + 1.5rem))',
        }}
      >
        {notes.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            You're all caught up.
          </p>
        ) : (
          <ol className="flex flex-col gap-6">
            {notes.map((note) => (
              <li
                key={note.version}
                className="flex flex-col gap-3 not-last:pb-6 not-last:border-b not-last:border-border/40"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold text-foreground">
                    {note.title ?? `Version ${note.version}`}
                  </h3>
                  {note.date && (
                    <time className="shrink-0 text-xs text-muted-foreground" dateTime={note.date}>
                      {formatDate(note.date)}
                    </time>
                  )}
                </div>
                <ul className="flex flex-col gap-2.5">
                  {note.items.map((item, i) => {
                    const meta = CATEGORY_META[item.category];
                    const Icon = meta.icon;
                    return (
                      <li key={i} className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ${meta.dotClass}`}
                          aria-hidden
                        >
                          <Icon className="h-3 w-3" />
                        </span>
                        <span className="text-[13px] leading-relaxed text-foreground">
                          <span className="sr-only">{meta.label}: </span>
                          {item.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </div>
    </DrawerCard>
  );
}
